import React, { useState, useContext } from 'react';
import styled from '@emotion/styled';
import {
  OutboundLinkButton,
  routes,
  PageContext,
  experiment,
  appendAmplitudeUserProperty
} from 'gatsby-theme-apollo';
import VisibilitySensor from 'react-visibility-sensor';

const StyledCTA = styled.div(props => ({
  textAlign: `center`,
  flexDirection: `column`,
  justifyContent: `center`,
  alignItems: `center`,
  display: `flex`,
  borderRadius: props.theme.radius
}));

const campaigns = {
  Discord: {
    pitchText: ({ gameName, entityName }) => {
      return gameName
        ? `Want to get better at ${gameName}${entityName ? ` with ${entityName}` : ``}?`
        : `We're building the best gaming site and need your ideas!`;
    },
    btnText: 'Join us on Discord',
    href: 'discord'
  },
  PlayElementArena: {
    experiments: {
      pitchText: 'element-arena-pitch-text-exp-1',
      btnText: `element-arena-btn-text-exp-1`
    },
    pitchText: () => {
      return 'Element Arena in Early Access now!';
    },
    videoURI: () => {
      return `https://www.youtube.com/watch?v=la4spaP0MaM`;
    },
    btnText: 'Play Now!',
    href: `https://elementarena.com`
  },
  Feedback: {
    pitchText: () => {
      return `How can we improve GamerHub?`;
    },
    btnText: 'Let us know!',
    href: `https://docs.google.com/forms/d/e/1FAIpQLSexzTrt00C1w6Wsq7_Esc9bdvcYeWLYNa1M3Z41Xw_-YhBXBA/viewform`
  },
  ElementArena: {
    pitchText: () => {
      return `Looking for a new 5V5 Moba on Mobile?`;
    },
    btnText: 'Check out Element Arena!',
    imageURI:
      'https://gamerhub-uploads.s3.us-west-1.amazonaws.com/9888f3820f1048208fdf84b25c6f6da3.png',
    href: `https://elementarena.com`
  },
  WriteForUs: {
    pitchText: ({ gameName }) => {
      return `GamerHub is building a passionate ${gameName} writing team! Interested?`;
    },
    btnText: 'Drop us a line',
    href: `mailto:hello@gamerhub.gg`
  },
  MakeTierList: {
    // eslint-disable-next-line no-unused-vars
    pitchText: ({ gameName, entityName, patchVersion }) => {
      if (entityName) {
        return `Rank ${entityName} in your your Tier List`;
      }
      return patchVersion
        ? `Does this change the meta? Be the first to make an updated Tier List!`
        : `Make your ${gameName} Tier List`;
    },
    btnText: 'Tier List Maker',
    route: gameName => {
      return routes.TierListEditor(gameName);
    }
  },
  MakeGuide: {
    // eslint-disable-next-line no-unused-vars
    pitchText: ({ gameName, entityName, postID }) => {
      if (postID) {
        return `Made with Guide Builder. Make your own ${gameName} guide!`;
      }
      return entityName ? `Make your guide for ${entityName}` : `Make a ${gameName} guide`;
    },
    btnText: 'Guide Builder',
    route: gameName => {
      return routes.GuideBuilder(gameName);
    }
  },
  ViewTierLists: {
    pitchText: ({ gameName, entityName }) => {
      return !entityName
        ? `See the latest Tier Lists for ${gameName}`
        : `See how ${entityName} ranks in the latest Tier Lists`;
    },
    btnText: ({ gameName }) => {
      return `${gameName} Tier Lists`;
    },
    route: gameName => {
      return routes.TierListHub(gameName);
    }
  }
};

const CTA = ({ campaignKey, providedCampaignData }) => {
  const { logEvent, gameName, discordInvite } = useContext(PageContext);
  const [seen, setSeen] = useState(false);

  const url = typeof window !== 'undefined' && window.location && window.location.href;

  let fontSize = `16px`;

  let computedCampaign = {
    gameName,
    ...providedCampaignData
  };

  const campaignTemplate = campaignKey && campaigns[campaignKey];
  let experimentParticipations = campaignTemplate.experiments;

  let pitchText = campaignTemplate.pitchText(computedCampaign);
  let videoURI = campaignTemplate.videoURI && campaignTemplate.videoURI(computedCampaign);

  let btnText =
    typeof campaignTemplate.btnText === 'function'
      ? campaignTemplate.btnText(computedCampaign)
      : campaignTemplate.btnText;

  const experimentSet = campaignTemplate.experiments;

  if (experimentSet) {
    const fontSizeExperimentKey = experimentSet['fontSize'];

    if (fontSizeExperimentKey) {
      fontSize = experiment(fontSizeExperimentKey) || fontSize;
    }

    const btnTextExperimentKey = experimentSet['btnText'];
    if (btnTextExperimentKey) {
      btnText = experiment(btnTextExperimentKey) || fontSize;
    }

    const pitchTextExperimentKey = experimentSet['pitchText'];
    if (pitchTextExperimentKey) {
      pitchText = experiment(pitchTextExperimentKey) || pitchText;
    }
  }

  const { imageURI } = campaignTemplate;

  let ctaLinkTarget = campaignTemplate.href;

  if (campaignTemplate.href === 'discord') {
    ctaLinkTarget = discordInvite
      ? `https://www.discord.gg/${discordInvite}`
      : 'https://discord.gg/WWgcW8Z';
  }

  ctaLinkTarget = ctaLinkTarget || campaignTemplate.route(gameName);
  computedCampaign = {
    href: campaignTemplate.href,
    ...computedCampaign,
    experimentParticipations,
    btnText,
    pitchText,
    videoURI,
    url,
    imageURI,
    fontSize,
    ctaLinkTarget
  };

  const [campaign, ] = useState(computedCampaign);

  const onVisibilityChanged = isVisible => {
    if (!seen && isVisible) {
      logEvent('cta_seen', {
        campaignKey,
        campaignData: campaign
      });
      setSeen(true);
    }
  };

  const Convert = () => {
    appendAmplitudeUserProperty('cta_conversions', campaignKey);
    logEvent(`cta_convert`, {
      campaignKey,
      campaignData: campaign
    });
    if (campaign && campaign.onConvert) {
      campaign.onConvert();
    }
  };

  return (
    <>
      {campaign && (
        <VisibilitySensor onChange={onVisibilityChanged}>
          <StyledCTA>
            {campaign.pitchText && (
              <span style={{ padding: `20px`, fontSize: campaign.fontSize }}>
                {campaign.pitchText}
              </span>
            )}
            {campaign.imageURI && <img alt={campaign.pitchText} src={campaign.imageURI} />}
            <OutboundLinkButton
              href={campaign.ctaLinkTarget}
              target={campaign.href && '_blank'}
              rel={campaign.href && 'noopener'}
              primary
              style={{ padding: `8px 15px` }}
              onClick={() => {
                Convert();
              }}
            >
              <span style={{ fontSize: campaign.fontSize, fontFamily: 'inherit' }}>
                {campaign.btnText}
              </span>
            </OutboundLinkButton>
          </StyledCTA>
        </VisibilitySensor>
      )}
    </>
  );
};

export default CTA;
